import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  IOnboardingFlow,
  IOnboardingFlowDraft,
  OnboardingFlowNode,
} from '@scale/shared/onboardingControlPanel';
import {
  getBypassWhitelistEntriesForFlowAndStep,
  setBypassWhitelistEmailsForFlowIdAndStepName,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';

type BypassWhitelistStore = {
  flowId: string;
  whitelist: { [key: string]: Set<string> };
  addEmails: (stepName: string, emails: string[]) => void;
  removeEmails: (stepName: string, emails: string[]) => void;
  initializeBypassWhitelist: (flow: IOnboardingFlow | IOnboardingFlowDraft<string>) => void;
  loadBypassWhitelist: (flow: IOnboardingFlow | IOnboardingFlowDraft<string>) => Promise<void>;
  addEmailByStepName: (stepName: string, email: string) => boolean;
  deleteEmailAndStepName: (stepName: string, email: string) => void;
  publishBypassWhitelistForStep: (
    stepName: string,
    restrictToTestAccounts?: boolean,
  ) => Promise<void>;
  publishBypassWhitelist: () => Promise<void>;
};

export const useBypassWhitelistStore = create<
  BypassWhitelistStore,
  [['zustand/persist', BypassWhitelistStore]]
>(
  persist(
    (set, get) => ({
      flowId: '',
      whitelist: {},
      addEmails: (stepName, emails) =>
        set({
          whitelist: {
            ...get().whitelist,
            [stepName]: new Set([...(get().whitelist[stepName] || new Set()), ...emails]),
          },
        }),
      removeEmails: (stepName, emails) => {
        const currentEmails = get().whitelist[stepName] || new Set();
        const updatedEmails = new Set([...currentEmails].filter(email => !emails.includes(email)));
        set({
          whitelist: {
            ...get().whitelist,
            [stepName]: updatedEmails,
          },
        });
      },
      initializeBypassWhitelist: (flow: IOnboardingFlow | IOnboardingFlowDraft<string>) => {
        set({ whitelist: {} });
        set({ flowId: flow._id });
      },
      loadBypassWhitelist: async (flow: IOnboardingFlow | IOnboardingFlowDraft<string>) => {
        set({ whitelist: {} });
        set({ flowId: flow._id });
        const promises = flow.nodes.map((step: OnboardingFlowNode) =>
          getBypassWhitelistEntriesForFlowAndStep(flow._id, step.name).then(emails => {
            return set(state => ({
              whitelist: {
                ...state.whitelist,
                [step.name]: new Set(emails),
              },
            }));
          }),
        );
        await Promise.all(promises);
      },
      addEmailByStepName: (stepName, email) => {
        const currentEmails = get().whitelist[stepName] || new Set();
        if (currentEmails.has(email)) {
          return false;
        }
        set(state => ({
          whitelist: {
            ...state.whitelist,
            [stepName]: new Set([...(state.whitelist[stepName] || new Set()), email]),
          },
        }));
        return true;
      },
      deleteEmailAndStepName: (stepName, email) =>
        set(state => {
          const currentEmails = state.whitelist[stepName] || new Set();
          currentEmails.delete(email);
          return {
            whitelist: {
              ...state.whitelist,
              [stepName]: currentEmails,
            },
          };
        }),
      publishBypassWhitelistForStep: async (stepName: string, restrictToTestAccounts?: boolean) => {
        const emails = Array.from(get().whitelist[stepName] || new Set());
        const response = await setBypassWhitelistEmailsForFlowIdAndStepName(
          get().flowId,
          stepName,
          emails,
          restrictToTestAccounts,
        );
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message);
        }
        return response;
      },
      publishBypassWhitelist: async () => {
        const promises = Object.keys(get().whitelist).map(stepName => {
          const emails = Array.from(get().whitelist[stepName] || new Set());
          return setBypassWhitelistEmailsForFlowIdAndStepName(get().flowId, stepName, emails);
        });
        await Promise.all(promises);
      },
    }),
    {
      name: 'bypass-whitelist-store',
    },
  ),
);
